import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Mail, Printer, Eye, Sparkles, Loader2, CheckCircle2, FileText } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useSharedData } from "./AgreementsPage";
import { supabase } from "@/integrations/supabase/client";
import { generateChecklistWordDocument } from "@/utils/checklistLetterWordGenerator";

interface ChecklistItem {
  id: string;
  text: string;
}

interface ChecklistLetterProps {
  preLoadedDeal?: {
    seller_name?: string;
    seller_email?: string;
    seller_address?: string;
    property_address?: string;
  };
}

// Default checklist items commonly needed for real estate note purchases
const DEFAULT_CHECKLIST_ITEMS = [
  "Sign and return enclosed Purchase Agreement",
  "Proof of the last 6 months payments (copies of cancelled checks, bank statements, venmo etc.)",
  "Complete and Return Contract Verification form",
  "Complete and return RESPA Compliance form",
  "Copy of original Land Contract or Mortgage",
  "Payment history/ledger for the past 12 months",
  "Current property tax statement",
  "Proof of hazard insurance on the property",
  "Buyer's contact information (phone, email, address)",
  "Copy of deed to the property",
  "Title insurance policy or commitment",
];

const ChecklistLetter = ({ preLoadedDeal }: ChecklistLetterProps = {}) => {
  const { toast } = useToast();
  const sharedDataContext = useSharedData();
  const { checklistData: letterData, setChecklistData: setLetterData, agreementData } = sharedDataContext;
  const [newItemText, setNewItemText] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('checklistLetterData');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setLetterData(parsed);
      } catch (e) {
        console.error('Failed to parse saved checklist data:', e);
      }
    }
  }, []);

  // Save to localStorage whenever letterData changes
  useEffect(() => {
    localStorage.setItem('checklistLetterData', JSON.stringify(letterData));
  }, [letterData]);

  // Initialize with preloaded deal data if available
  useEffect(() => {
    if (preLoadedDeal) {
      setLetterData(prev => ({
        ...prev,
        recipientName: preLoadedDeal.seller_name || prev.recipientName,
        recipientEmail: preLoadedDeal.seller_email || prev.recipientEmail,
        recipientAddress: preLoadedDeal.seller_address || prev.recipientAddress,
      }));
    }
  }, [preLoadedDeal, setLetterData]);

  const addChecklistItem = () => {
    if (newItemText.trim()) {
      const newItem: ChecklistItem = {
        id: Date.now().toString(),
        text: newItemText.trim()
      };
      setLetterData(prev => ({
        ...prev,
        checklistItems: [...prev.checklistItems, newItem]
      }));
      setNewItemText('');
    }
  };

  const addDefaultItem = (itemText: string) => {
    const newItem: ChecklistItem = {
      id: Date.now().toString(),
      text: itemText
    };
    setLetterData(prev => ({
      ...prev,
      checklistItems: [...prev.checklistItems, newItem]
    }));
    toast({
      title: "Item Added",
      description: "Checklist item added successfully."
    });
  };

  const removeChecklistItem = (id: string) => {
    setLetterData(prev => ({
      ...prev,
      checklistItems: prev.checklistItems.filter(item => item.id !== id)
    }));
  };

  const handlePrint = () => {
    window.print();
    toast({
      title: "Print Dialog Opened",
      description: "The letter is ready to print."
    });
  };

  const handleEmail = () => {
    // TODO: Implement email functionality with edge function
    toast({
      title: "Email Feature",
      description: "Email functionality will be implemented soon."
    });
  };

  const handleOpenInWord = async () => {
    try {
      const blob = await generateChecklistWordDocument(letterData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Checklist_Letter_${letterData.recipientName || 'Document'}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Document Generated",
        description: "Your checklist letter has been downloaded as a Word document."
      });
    } catch (error) {
      console.error('Error generating Word document:', error);
      toast({
        title: "Error",
        description: "Failed to generate Word document. Please try again.",
        variant: "destructive"
      });
    }
  };

  const updateField = (field: keyof typeof letterData, value: string) => {
    setLetterData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateChecklistItems = async () => {
    if (!agreementData.propertyAddress || !agreementData.balance) {
      toast({
        title: "Missing Information",
        description: "Please fill in property address and balance in the agreement form first.",
        variant: "destructive",
      });
      return;
    }

    setAiGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-agreement-content', {
        body: {
          type: 'checklist_items',
          dealData: {
            propertyAddress: agreementData.propertyAddress,
            balance: agreementData.balance,
          },
          agreementType: agreementData.agreementType,
        },
      });

      if (error) throw error;
      
      // Parse the AI response and convert to checklist items
      const items = data.content
        .split('\n')
        .filter((line: string) => line.trim().startsWith('•') || line.trim().startsWith('-'))
        .map((line: string, index: number) => ({
          id: `${Date.now()}_${index}`,
          text: line.replace(/^[•\-]\s*/, '').trim()
        }));

      setLetterData(prev => ({
        ...prev,
        checklistItems: items
      }));

      toast({
        title: "AI Checklist Generated! ✨",
        description: `${items.length} checklist items created.`,
      });
    } catch (error) {
      console.error('Error generating checklist:', error);
      toast({
        title: "Generation Failed",
        description: "There was an error generating the checklist. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const LetterPreview = () => (
    <div className="max-w-[8.5in] mx-auto p-12 bg-white text-black print:p-16">
      <div className="text-center mb-10">
        <img 
          src="/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" 
          alt="Contract Funding Group Logo" 
          className="mx-auto mb-4"
          style={{ width: '200px', height: 'auto' }}
        />
        <h1 className="text-2xl font-bold text-[#8B4513]">CONTRACT FUNDING GROUP, LLC</h1>
        <p className="text-[#FF6B35] font-semibold">Making real estate investments simple</p>
      </div>
      
      <div className="mb-8">
        <p className="mb-4 text-sm">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <div>
          <p className="font-semibold text-base">{letterData.recipientName || '[Recipient Name]'}</p>
          {letterData.recipientAddress && (
            <p className="whitespace-pre-line text-sm">{letterData.recipientAddress}</p>
          )}
        </div>
      </div>

      <div className="mb-8 space-y-4 text-base leading-relaxed">
        <p>Dear {letterData.recipientName || '[Recipient Name]'},</p>
        
        <p>
          Thank you for choosing Contract Funding Group. We strive to make the closing process 
          efficient and painless for all parties involved.
        </p>
        
        <p>
          Below is a checklist of items needed for your cash offer underwriting. Please gather 
          these documents to ensure a smooth and timely closing process:
        </p>
      </div>

      <div className="mb-10">
        <h3 className="text-lg font-semibold mb-6 text-[#8B4513]">Required Documentation Checklist:</h3>
        {letterData.checklistItems.length > 0 ? (
          <ul className="space-y-3">
            {letterData.checklistItems.map((item) => (
              <li key={item.id} className="flex items-start">
                <span className="inline-block w-5 h-5 border-2 border-gray-400 mr-4 mt-1 flex-shrink-0 print:border-black"></span>
                <span className="text-base">{item.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 italic">No checklist items added yet.</p>
        )}
      </div>

      <div className="mb-8 space-y-4 text-base leading-relaxed">
        <p>
          Please review this checklist carefully and gather all required documents. If you have 
          any questions or need clarification on any item, please don't hesitate to contact us.
        </p>
        
        <p>
          We appreciate your business and look forward to working with you to complete this transaction.
        </p>
      </div>

      <div className="mt-12">
        <p className="mb-2 text-base">Sincerely,</p>
        <div className="mt-12">
          <div className="border-b border-black w-48 mb-1"></div>
          <p className="font-semibold text-base">Contract Funding Group Team</p>
        </div>
        <p className="text-sm text-gray-600 mt-8">
          Contract Funding Group | Real Estate Investment Solutions
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Checklist Letter Generator</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Letter Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="recipientName">Recipient Name</Label>
              <Input
                id="recipientName"
                value={letterData.recipientName}
                onChange={(e) => updateField('recipientName', e.target.value)}
                placeholder="Enter recipient's full name"
              />
            </div>
            
            <div>
              <Label htmlFor="recipientEmail">Recipient Email</Label>
              <Input
                id="recipientEmail"
                type="email"
                value={letterData.recipientEmail}
                onChange={(e) => updateField('recipientEmail', e.target.value)}
                placeholder="Enter recipient's email"
              />
            </div>
            
            <div>
              <Label htmlFor="recipientAddress">Recipient Address</Label>
              <Textarea
                id="recipientAddress"
                value={letterData.recipientAddress}
                onChange={(e) => updateField('recipientAddress', e.target.value)}
                placeholder="Enter recipient's mailing address"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Checklist Items</span>
              <Button
                onClick={handleGenerateChecklistItems}
                disabled={aiGenerating}
                variant="outline"
                size="sm"
                className="border-purple-300 hover:bg-purple-100"
              >
                {aiGenerating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> AI Generate Items</>
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick Add Default Items */}
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Quick Add Common Items
              </h4>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                {DEFAULT_CHECKLIST_ITEMS.map((item, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => addDefaultItem(item)}
                    className="justify-start text-left h-auto py-2 px-3 hover:bg-primary/10"
                  >
                    <Plus className="h-3 w-3 mr-2 flex-shrink-0" />
                    <span className="text-xs">{item}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Manual Add */}
            <div className="flex gap-2">
              <Input
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                placeholder="Or type a custom checklist item"
                onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
              />
              <Button onClick={addChecklistItem} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Current Items List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {letterData.checklistItems.map((item) => (
                <div key={item.id} className="flex items-center gap-2 p-2 border rounded">
                  <span className="flex-1 text-sm">{item.text}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeChecklistItem(item.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {letterData.checklistItems.length === 0 && (
                <p className="text-muted-foreground text-sm text-center py-4">
                  No checklist items added yet. Click items above or type custom ones.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Letter
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Letter Preview</DialogTitle>
                </DialogHeader>
                <LetterPreview />
              </DialogContent>
            </Dialog>
            
            <Button onClick={handleOpenInWord} variant="outline">
              <FileText className="h-4 w-4 mr-2" />
              Open in Word
            </Button>

            <Button onClick={handlePrint} variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Print Letter
            </Button>
            
            <Button onClick={handleEmail} disabled>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Hidden print version */}
      <div className="hidden print:block">
        <LetterPreview />
      </div>
    </div>
  );
};

export default ChecklistLetter;